import {
    buildMultiSelectConfirmedAgentMessage,
    buildMultiSelectDisplayText,
    formatSelectedLabels,
    toggleMultiSelectOption,
} from '@fruitful/llm-service-shared';
import { type MultiSelectInputWidgetProps } from '@fruitful/types';
import React, { useCallback, useEffect, useState } from 'react';
import { Platform, ScrollView as RNScrollView } from 'react-native';
import {
    ArrowUpIcon,
    BodyMediumRegular,
    CircularSelectionIndicator,
    H6Regular,
    Icon,
    IconButton,
    Input,
    PencilIcon,
    ScrollView,
    XStack,
    YStack,
} from 'smoothie';

import { CHAT_INPUT_WIDGET } from './shared-chat/ChatLayout';
import { BaseInputWidget } from './widgets/BaseInputWidget';

/** Fallback cap used when we can't measure the real visual viewport (e.g. native). */
const FOOTER_OPTIONS_MIN_HEIGHT_KEYBOARD = 160;
/** Space reserved for the custom-input row + surface/frame padding around the options list. */
const FOOTER_OPTIONS_RESERVED_CHROME_HEIGHT = 120;
const COMPOSER_LEADING_SLOT_SIZE = 40;

interface MultiSelectInputWidgetComponentProps extends MultiSelectInputWidgetProps {
    disabled?: boolean;
    isActive?: boolean;
    isKeyboardVisible?: boolean;
    variant?: 'inline' | 'footer';
    onSubmit: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
}

const KEYBOARD_OPEN_THRESHOLD = 0.75;

interface WebKeyboardState {
    visible: boolean;
    viewportHeight: number | null;
}

const useWebKeyboardState = (): WebKeyboardState => {
    const [state, setState] = useState<WebKeyboardState>({
        visible: false,
        viewportHeight: null,
    });

    useEffect(() => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') {
            return undefined;
        }

        const visualViewport = window.visualViewport;
        if (visualViewport == null) {
            return undefined;
        }

        const initialHeight = window.innerHeight;

        const handleResize = () => {
            const visible =
                visualViewport.height < initialHeight * KEYBOARD_OPEN_THRESHOLD;
            setState({
                visible,
                viewportHeight: visible ? visualViewport.height : null,
            });
        };

        visualViewport.addEventListener('resize', handleResize);
        return () => {
            visualViewport.removeEventListener('resize', handleResize);
        };
    }, []);

    return state;
};

export const MultiSelectInputWidget = ({
    options,
    responseKey,
    minSelections = 1,
    maxSelections,
    allowCustomInput = false,
    customInputPlaceholder = 'Type your answer...',
    preSelectedOptionIds,
    disabled = false,
    isActive = true,
    isKeyboardVisible: isKeyboardVisibleProp = false,
    variant = 'inline',
    promptText,
    onSubmit,
}: MultiSelectInputWidgetComponentProps) => {
    const {
        visible: webKeyboardVisible,
        viewportHeight: webKeyboardViewportHeight,
    } = useWebKeyboardState();
    const isKeyboardVisible = isKeyboardVisibleProp || webKeyboardVisible;
    const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(
        preSelectedOptionIds ?? [],
    );
    const [customInputValue, setCustomInputValue] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const isWidgetDisabled = disabled || !isActive || hasSubmitted;

    const handleToggleOption = useCallback(
        (optionId: string) => {
            if (isWidgetDisabled) {
                return;
            }

            setSelectedOptionIds((previous) =>
                toggleMultiSelectOption(previous, optionId, maxSelections),
            );
        },
        [isWidgetDisabled, maxSelections],
    );

    const submitSelection = useCallback(
        (displayText: string, inputText: string, optionIds: string[]) => {
            if (isWidgetDisabled || displayText.trim() === '') {
                return;
            }

            const agentPayload = buildMultiSelectConfirmedAgentMessage({
                displayText,
                payload: {
                    responseKey,
                    selectedOptionIds: optionIds,
                    inputText,
                },
            });

            setHasSubmitted(true);
            void onSubmit(displayText, agentPayload);
        },
        [isWidgetDisabled, onSubmit, responseKey],
    );

    const handleInlineSend = useCallback(() => {
        const customTrimmed = customInputValue.trim();
        const labelText = formatSelectedLabels(options, selectedOptionIds);
        const displayText = buildMultiSelectDisplayText(
            labelText,
            customTrimmed,
        );
        const inputText = customTrimmed;

        submitSelection(displayText, inputText, selectedOptionIds);
    }, [customInputValue, options, selectedOptionIds, submitSelection]);

    const showCustomInputRow = allowCustomInput;
    // A pure single-select with no free-text option (e.g. a yes/no question)
    // submits the moment an option is tapped — no send button, no dangling row.
    const autoSubmitOnSelect = !allowCustomInput && maxSelections === 1;
    const canSend =
        !hasSubmitted &&
        !isWidgetDisabled &&
        (selectedOptionIds.length >= minSelections ||
            customInputValue.trim() !== '');

    const isFooterVariant = variant === 'footer';
    const optionsMaxHeight =
        isFooterVariant && isKeyboardVisible
            ? Math.max(
                  FOOTER_OPTIONS_MIN_HEIGHT_KEYBOARD,
                  (webKeyboardViewportHeight ?? 0) -
                      FOOTER_OPTIONS_RESERVED_CHROME_HEIGHT,
              )
            : undefined;

    const optionRows = options.map((option) => {
        const isSelected = selectedOptionIds.includes(option.id);

        return (
            <XStack
                key={option.id}
                alignItems="center"
                gap={CHAT_INPUT_WIDGET.rowGap}
                paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                paddingVertical={CHAT_INPUT_WIDGET.rowPaddingVertical}
                disabled={isWidgetDisabled}
                cursor={isWidgetDisabled ? 'default' : 'pointer'}
                pointerEvents={isWidgetDisabled ? 'none' : 'auto'}
                onPress={() => {
                    if (autoSubmitOnSelect) {
                        submitSelection(option.label, '', [option.id]);
                        return;
                    }
                    handleToggleOption(option.id);
                }}
                pressStyle={
                    !isWidgetDisabled && {
                        opacity: 0.7,
                    }
                }
            >
                <CircularSelectionIndicator
                    selected={isSelected}
                    disabled={isWidgetDisabled}
                    borderVisible
                />
                <YStack
                    flex={1}
                    minHeight={CHAT_INPUT_WIDGET.rowMinHeight}
                    justifyContent="center"
                    pointerEvents="none"
                >
                    <BodyMediumRegular>{option.label}</BodyMediumRegular>
                </YStack>
            </XStack>
        );
    });

    const optionsContent =
        optionsMaxHeight != null ? (
            Platform.OS === 'web' ? (
                <ScrollView maxHeight={optionsMaxHeight}>
                    {optionRows}
                </ScrollView>
            ) : (
                <RNScrollView
                    style={{ maxHeight: optionsMaxHeight }}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                >
                    {optionRows}
                </RNScrollView>
            )
        ) : (
            optionRows
        );

    return (
        <BaseInputWidget
            alignSelf={isFooterVariant ? 'stretch' : 'center'}
            width="100%"
            maxWidth={isFooterVariant ? '100%' : CHAT_INPUT_WIDGET.maxWidth}
            paddingTop={CHAT_INPUT_WIDGET.paddingTop}
            paddingBottom={
                showCustomInputRow ? 0 : CHAT_INPUT_WIDGET.paddingBottom
            }
        >
            {promptText != null && (
                <H6Regular
                    paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingBottom={12}
                >
                    {promptText}
                </H6Regular>
            )}
            {optionsContent}

            {autoSubmitOnSelect ? null : showCustomInputRow ? (
                <XStack
                    alignItems="center"
                    gap={CHAT_INPUT_WIDGET.rowGap}
                    minHeight={48}
                    paddingLeft={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingRight={8}
                    paddingTop={CHAT_INPUT_WIDGET.rowPaddingVertical}
                    paddingBottom={8}
                >
                    <XStack
                        width={COMPOSER_LEADING_SLOT_SIZE}
                        height={COMPOSER_LEADING_SLOT_SIZE}
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                    >
                        <Icon icon={PencilIcon} size={16} color="$gray1000" />
                    </XStack>
                    <XStack
                        flex={1}
                        minHeight={CHAT_INPUT_WIDGET.rowMinHeight}
                        alignItems="center"
                    >
                        <Input
                            flex={1}
                            width="100%"
                            size="regular"
                            fontWeight="regular"
                            placeholder={customInputPlaceholder}
                            placeholderTextColor="$gray1000"
                            value={customInputValue}
                            disabled={isWidgetDisabled}
                            height={40}
                            padding={0}
                            paddingLeft={0}
                            paddingRight={8}
                            paddingVertical={0}
                            borderWidth={0}
                            backgroundColor="transparent"
                            focusStyle={{
                                borderWidth: 0,
                                outlineWidth: 0,
                            }}
                            hoverStyle={{
                                borderWidth: 0,
                                outlineWidth: 0,
                            }}
                            containerProps={{
                                flex: 1,
                                width: '100%',
                                height: 40,
                                alignItems: 'center',
                                backgroundColor: 'transparent',
                            }}
                            onChangeText={setCustomInputValue}
                            onSubmitEditing={handleInlineSend}
                        />
                    </XStack>
                    <IconButton
                        variant="primary"
                        icon={ArrowUpIcon}
                        disabled={!canSend}
                        onPress={handleInlineSend}
                    />
                </XStack>
            ) : (
                <XStack
                    alignItems="center"
                    justifyContent="flex-end"
                    minHeight={48}
                    paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingVertical={CHAT_INPUT_WIDGET.rowPaddingVertical}
                >
                    <IconButton
                        variant="primary"
                        icon={ArrowUpIcon}
                        disabled={!canSend}
                        onPress={handleInlineSend}
                    />
                </XStack>
            )}
        </BaseInputWidget>
    );
};
